import { cva, type VariantProps } from 'class-variance-authority'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  Lightbulb,
  type LucideIcon,
} from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from './Icon'

/**
 * Alert — inline notice. Pencil xRAcR xyGsN 7dIff QEIqZ
 * (Warning/Error/Info/Success).
 *
 * Markdoc callouts use the same component via the {% callout type=… %} tag.
 * Tone names map directly to callout types:
 *   {% callout type="info"   %}  -> tone="info"
 *   {% callout type="warn"   %}  -> tone="warning"
 *   {% callout type="danger" %}  -> tone="error"
 *   {% callout type="tip"    %}  -> tone="tip"      (Markdoc-only; lightbulb)
 *   {% callout type="success"%}  -> tone="success"
 */
export const alertVariants = cva(
  'relative w-full rounded-md border px-4 py-3 flex gap-3 items-start',
  {
    variants: {
      tone: {
        info: 'bg-ia-info-bg border-ia-info/20 text-ia-text',
        success: 'bg-ia-success-bg border-ia-success/20 text-ia-text',
        warning: 'bg-ia-warning-bg border-ia-warning/20 text-ia-text',
        error: 'bg-ia-error-bg border-ia-error/20 text-ia-text',
        tip: 'bg-ia-green-muted border-ia-green/20 text-ia-text',
      },
    },
    defaultVariants: { tone: 'info' },
  },
)

const ICONS: Record<
  NonNullable<VariantProps<typeof alertVariants>['tone']>,
  { icon: LucideIcon; color: string }
> = {
  info: { icon: Info, color: 'text-ia-info' },
  success: { icon: CheckCircle2, color: 'text-ia-success' },
  warning: { icon: AlertTriangle, color: 'text-ia-warning' },
  error: { icon: AlertCircle, color: 'text-ia-error' },
  tip: { icon: Lightbulb, color: 'text-ia-green' },
}

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: React.ReactNode
  icon?: LucideIcon
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, tone = 'info', title, icon, children, ...props }, ref) => {
    const { icon: DefaultIcon, color } = ICONS[tone ?? 'info']
    return (
      <div ref={ref} role="alert" className={cn(alertVariants({ tone }), className)} {...props}>
        <div className={cn('flex-shrink-0 pt-0.5', color)}>
          <Icon icon={icon ?? DefaultIcon} size="md" />
        </div>
        <div className="flex-1">
          {title && <div className="text-body-sm font-semibold mb-1">{title}</div>}
          {children && <div className="text-body-sm text-ia-text-secondary">{children}</div>}
        </div>
      </div>
    )
  },
)
Alert.displayName = 'Alert'
